// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useState, useCallback, useEffect, useMemo } from 'react';
import { getDocs, onSnapshot } from 'firebase/firestore';

export function useGetCollection(ref, { withSnapshot = false, initialCall = true, dependencies = [] } = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(initialCall);
  const [error, setError] = useState(null);

  const stableRef = useMemo(() => ref, [JSON.stringify(ref)]);

  const getCollection = useCallback(async () => {
    if (!stableRef) return null;
    setLoading(true);
    setError(null);
    try {
      const snapshot = await getDocs(stableRef);
      const collectionData = !snapshot.empty
        ? snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        : [];
      setData(collectionData);
      return collectionData;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [stableRef]);

  const getCollectionWithSnapshot = useCallback(() => {
    if (!stableRef) return () => {};
    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      stableRef,
      (snapshot) => {
        const collectionData = !snapshot.empty
          ? snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
          : [];
        setData(collectionData);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [stableRef]);

  useEffect(() => {
    if (!initialCall) return;

    if (withSnapshot) {
      const unsubscribe = getCollectionWithSnapshot();
      return () => unsubscribe?.();
    } else {
      getCollection();
    }
  }, [stableRef, withSnapshot, initialCall, ...dependencies]);

  return { data, loading, error, getCollection: withSnapshot ? getCollectionWithSnapshot : getCollection };
}
